<template lang="pug">
  transition(appear)
    .MapContainer
      MapboxMap(
        :access-token="accessToken"
        :map-style="mapStyle"
        @mb-movestart="handleMoveStart"
        @mb-moveend="handleMoveEnd"
        @mb-load="onMapLoaded"
        :dragRotate="false"
        :zoom="3"
        :minZoom="3"
        )
        MapboxCluster(:data="toGeojson(items)"
                      :clusterRadius="55"
                      :clustersPaint="clustersPaint"
                      :clusterCountPaint="clusterCountPaint"
                      :unclusteredPointPaint="unclusteredPointPaint"
                      @mb-feature-click="handleFeatureClick"
                      )

      transition(appear)
        .MapContainer_back(v-if="popup || noresult" @click="() => popup ? popup = false : null")

      transition(appear)
        .MapContainer_noresult(v-if="noresult")
          h2 {{ $t('search.mapNoresult') }}

      transition(appear)
        .MapContainer_cache(v-if="!mapLoaded")

      transition(appear)
        p.MapContainer_message(v-if="mapReady && maxRes")
          span.MapContainer_message_inner {{ $t('search.tooManyResults') }}

      MapPopup(v-if="popup" @close="closePopup" :data="popupData")
</template>

<script>
import { MapboxMap, MapboxCluster } from '@studiometa/vue-mapbox-gl'
import { mapGetters } from 'vuex'

import SvgInline from '@/components/SvgInline'
import MapPopup from '@/components/MapPopup'

export default {
  props: {
    items: {
      type: Array,
      default () {
        return []
      }
    },
    totalResults: {
      type: Number,
      default: 0
    }
  },
  components: {
    MapboxMap,
    MapboxCluster,
    SvgInline,
    MapPopup
  },
  watch: {
    totalResults () {
      this.maxRes = this.totalResults > this.items.length
    }
  },
  data () {
    return {
      accessToken: process.env.VUE_APP_MAPBOX_TOKEN,
      mapStyle: 'mapbox://styles/mapbox/dark-v10',
      popupData: null,
      popup: false,
      map: null,
      noresult: false,
      geoJsonSource: {
        'type': 'geojson',
        'data': 'https://docs.mapbox.com/mapbox-gl-js/assets/earthquakes.geojson'
      },
      clustersPaint: { 'circle-color': '#FFEFCE', 'circle-radius': 30 },
      clusterCountPaint: { 'text-color': '#000' },
      unclusteredPointPaint: { 'circle-color': '#FFEFCE', 'circle-radius': 7 },
      lastItems: null,
      mapLoaded: false,
      mapReady: false,
      maxRes: false,
      refZoom: 0,
      refX: 0,
      refY: 0
    }
  },
  computed: {
    ...mapGetters(['searchQuery'])
  },
  methods: {
    async onMapLoaded (e) {
      this.map = e.target

      const bounds = this.map.getBounds()

      this.actualBounds = {
        sw: {
          lat: bounds._sw.lat,
          lng: bounds._sw.lng
        },
        ne: {
          lat: bounds._ne.lat,
          lng: bounds._ne.lng
        }
      }

      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(this.successGeo, this.errorGeo)
      } else {
        this.errorGeo()
      }
    },
    successGeo (p) {
      this.mapLoaded = true
      this.map.flyTo({ center: [p.coords.longitude, p.coords.latitude], zoom: 5 })
    },
    errorGeo () {
      this.mapLoaded = true
      this.map.flyTo({ center: [2.825153, 46.561946], zoom: 5 })
    },
    toGeojson (items) {
      let geojson = {
        'type': 'FeatureCollection',
        'crs': { 'type': 'name', 'properties': { 'name': 'urn:ogc:def:crs:OGC:1.3:CRS84' } },
        'features': []
      }

      if (this.mapReady) {
        for (const item in items) {
          geojson.features.push({
            'type': 'Feature',
            'properties': { 'name': items[item]._data.name, 'url': items[item]._data.url, 'locality': items[item]._data.locality, 'country': items[item]._data.country, 'type': items[item]._data.type },
            'geometry': { 'type': 'Point', 'coordinates': [ items[item]._data.lng, items[item]._data.lat, 0.0 ] } })
        }
      }

      return geojson
    },
    calcCrow (lat1, lon1, lat2, lon2) {
      var R = 6371 // km
      var dLat = this.toRad(lat2 - lat1)
      var dLon = this.toRad(lon2 - lon1)
      var dLat1 = this.toRad(lat1)
      var dLat2 = this.toRad(lat2)

      var a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.sin(dLon / 2) * Math.sin(dLon / 2) * Math.cos(dLat1) * Math.cos(dLat2)
      var c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
      var d = R * c
      return d
    },
    toRad (v) {
      return v * Math.PI / 180
    },
    handleFeatureClick (e) {
      this.openPopup(e.properties)
    },
    closePopup () {
      this.popupData = null
      this.popup = false
    },
    openPopup (data) {
      this.popupData = data
      this.popup = true
    },
    handleMoveStart () {
      this.closePopup()
    },
    handleMoveEnd (e) {
      if (this.mapLoaded) {
        this.mapReady = true
      }
      const rawBounds = e.target.getBounds()
      const bounds = {
        sw: {
          lat: rawBounds._sw.lat,
          lng: rawBounds._sw.lng
        },
        ne: {
          lat: rawBounds._ne.lat,
          lng: rawBounds._ne.lng
        }
      }

      const zoomDiff = Math.abs(Number(this.refZoom - e.target.getZoom()))
      let xDiff = 0
      let yDiff = 0

      if (e.originalEvent) {
        xDiff = Math.abs(Number(this.refX - e.originalEvent.x))
        yDiff = Math.abs(Number(this.refY - e.originalEvent.y))
      }

      if ((zoomDiff >= 0.3 || xDiff > 50 || yDiff > 50) && this.mapLoaded) {
        this.$emit('coordsFilter', bounds)

        this.refZoom = e.target.getZoom()
        if (e.originalEvent) {
          this.refX = e.originalEvent.x
          this.refY = e.originalEvent.y
        }
      }
    }
  }
}
</script>

<style lang="stylus">
.MapContainer
  position absolute
  top 0
  left 0
  padding 0
  margin 0
  overflow hidden
  width 100%
  height 100%
  font-size 0
  scroll-behavior smooth
  transform scale(1)

  &.v-enter, &.v-leave-to
    opacity 0
    transform scale(1.5)

  &.v-enter-active, &.v-leave-active
    transition all 1.2s $ease

  &_message
    position absolute
    overflow hidden
    left 50%
    bottom: 20px
    transform translate(-50%, 0)
    padding 10px 20px
    background-color #000
    border-radius 30px
    text-align center
    opacity 0.9

    &_inner
      color white
      font-size 14px

    &.v-enter, &.v-leave-to
      bottom: -400px

    &.v-enter-active, &.v-leave-active
      transition all 0.6s $ease

  &_cache
    position absolute
    width 100%
    height 100%
    top 0
    left 0
    opacity 1
    background-color #000

    &.v-enter, &.v-leave-to
      opacity 0

    &.v-enter-active, &.v-leave-active
      transition all 0.6s $ease

  &_back
    position absolute
    width 100%
    height 100%
    top 0
    left 0
    opacity .65
    background-color #000

    &.v-enter, &.v-leave-to
      opacity 0

    &.v-enter-active, &.v-leave-active
      transition all 0.6s $ease

  &_noresult
    padding 20% 0
    width 100%
    box-sizing border-box
    position: absolute;
    top: 50%;
    transform translate(0%, -50%)

    &.v-enter, &.v-leave-to
      opacity 0
      transform translate(0%, 0%)

    &.v-enter-active, &.v-leave-active
      transition all .6s $ease

    @media $mq_s_only

      height 50vh
      top 50vh

    h2

      color white
      max-width 85%
      margin auto
      font-size: 2.5vw
      letter-spacing: 1.25px;
      text-align: center;

      @media $mq_s_only

        max-width 100%
        font-size 5vw

  .mapboxgl-map {
    margin: 0;
    width: 100%;
    height: 100%;
  }

  .mapboxgl-ctrl-bottom-right
    display none

  .Map-marker
    cursor pointer
    width 40px
    fill  $beige

    &:hover
      fill  $beige-l

</style>
